import { Link } from "react-router-dom";
import { Lock, MapPin, ExternalLink, Phone, Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AcceptingBadge, DemoBadge, Stars, VerifiedChip } from "@/components/Badges";
import { mediaUrl } from "@/lib/api";

const Tags = ({ items, max = 3 }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.slice(0, max).map((s) => <span key={s} className="rounded-md bg-slate-100 text-slate-700 text-xs font-medium px-2 py-1">{s}</span>)}
    {items.length > max && <span className="text-xs text-slate-500 px-1 py-1">+{items.length - max}</span>}
  </div>
);

export function LockedProviderCard({ p }) {
  return (
    <div className="cz-card overflow-hidden relative" data-testid="locked-provider-card">
      <div className="h-28 bg-gradient-to-br from-slate-200 to-teal-100 relative">
        <div className="absolute inset-0 backdrop-blur-md" />
        <div className="absolute -bottom-6 left-5 h-14 w-14 rounded-2xl bg-slate-300 border-4 border-white grid place-items-center"><Lock className="h-5 w-5 text-slate-500" /></div>
      </div>
      <div className="p-5 pt-9 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="h-5 w-40 rounded bg-slate-200 blur-[3px] select-none" aria-label="Provider name hidden" />
          {p.is_demo && <DemoBadge />}
        </div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{p.label}</div>
        <div className="flex flex-wrap items-center gap-2"><AcceptingBadge accepting={p.accepting_new_clients} size="sm" /><Stars value={p.rating_avg} count={p.rating_count} /></div>
        <Tags items={p.services} />
        <div className="flex items-start gap-1.5 text-sm text-slate-600"><MapPin className="h-4 w-4 mt-0.5 text-teal-600 shrink-0" />{p.service_area.slice(0, 2).join(" · ")}</div>
        {p.verifications?.length > 0 && <div className="flex flex-wrap gap-1.5">{p.verifications.map((v) => <VerifiedChip key={v.key} item={v} />)}</div>}
        <div className="grid grid-cols-2 gap-2 pt-1 select-none">
          <div className="h-9 rounded-full bg-slate-100 flex items-center justify-center gap-1.5 text-xs text-slate-400"><Phone className="h-3.5 w-3.5" /> Locked</div>
          <div className="h-9 rounded-full bg-slate-100 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ExternalLink className="h-3.5 w-3.5" /> Locked</div>
        </div>
      </div>
    </div>
  );
}

export default function ProviderCard({ p, onRequest, requested }) {
  return (
    <div className="cz-card cz-card-hover overflow-hidden flex flex-col" data-testid={`provider-card-${p.slug}`}>
      <div className="h-32 bg-slate-100 relative">
        {p.cover_url && <img src={mediaUrl(p.cover_url)} alt="" className="h-full w-full object-cover" loading="lazy" />}
        <div className="absolute top-3 left-3"><AcceptingBadge accepting={p.accepting_new_clients} size="sm" /></div>
        {p.is_demo && <DemoBadge className="absolute top-3 right-3" />}
        <div className="absolute -bottom-7 left-5 h-14 w-14 rounded-2xl bg-white border-4 border-white shadow overflow-hidden">
          {p.logo_url ? <img src={mediaUrl(p.logo_url)} alt={`${p.name} logo`} className="h-full w-full object-cover" /> : null}
        </div>
      </div>
      <div className="p-5 pt-10 flex-1 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/providers/${p.slug}`} className="font-display font-bold text-lg text-slate-900 hover:text-teal-700 leading-tight" data-testid="provider-card-name">{p.name}</Link>
          <Stars value={p.rating_avg} count={p.rating_count} />
        </div>
        <p className="text-sm text-slate-600 line-clamp-2">{p.short_description}</p>
        <Tags items={p.services} />
        <div className="flex items-start gap-1.5 text-sm text-slate-600"><MapPin className="h-4 w-4 mt-0.5 text-teal-600 shrink-0" />{p.service_area.slice(0, 3).join(" · ")}</div>
        {p.payment_methods?.length > 0 && <div className="text-xs text-slate-500"><span className="font-semibold text-slate-700">Payment:</span> {p.payment_methods.join(", ")}</div>}
        {p.verifications?.length > 0 && <div className="flex flex-wrap gap-1.5">{p.verifications.map((v) => <VerifiedChip key={v.key} item={v} />)}</div>}
        <div className="grid grid-cols-2 gap-2 mt-auto pt-2">
          <Button asChild variant="outline" className="rounded-full" data-testid="view-provider-btn"><Link to={`/providers/${p.slug}`}><Eye className="h-4 w-4 mr-1" />View Provider</Link></Button>
          <Button className="rounded-full bg-[#003B2D] hover:bg-[#00281F]" disabled={requested} onClick={() => onRequest?.(p)} data-testid="request-care-btn">
            <Send className="h-4 w-4 mr-1" />{requested ? "Requested" : "Request Care"}
          </Button>
          {p.website && <Button asChild variant="ghost" className="rounded-full text-teal-700" data-testid="visit-website-btn"><a href={p.website} target="_blank" rel="noreferrer noopener"><ExternalLink className="h-4 w-4 mr-1" />Visit Website</a></Button>}
          <Button variant="ghost" className="rounded-full text-teal-700" onClick={() => onRequest?.(p, "contact")} data-testid="contact-provider-btn"><Phone className="h-4 w-4 mr-1" />Contact Provider</Button>
        </div>
      </div>
    </div>
  );
}
